import { Config3D } from "../../../../Config3D";
import { ILaya } from "../../../../ILaya";
import { Sprite } from "../../../display/Sprite";
import { SubmitKey } from "../../../webgl/submit/SubmitKey";
import { Cluster } from "../../graphics/renderPath/Cluster";
import { PhysicsSettings } from "../../physics/PhysicsSettings";
import { SkyRenderer } from "../../resource/models/SkyRenderer";
import { Utils3D } from "../../utils/Utils3D";
import { BaseCamera } from "../BaseCamera";
import { AlternateLightQueue, LightQueue } from "../light/LightQueue";
import { RenderContext3D } from "../render/RenderContext3D";
import { Lightmap } from "./Lightmap";
import { Scene3DShaderDeclaration } from "./Scene3DShaderDeclaration";
import { ShadowCasterPass } from "../../shadowMap/ShadowCasterPass";
import { BlitFrameBufferCMD } from "../render/command/BlitFrameBufferCMD";
import { FilterMode } from "../../../RenderEngine/RenderEnum/FilterMode";
import { RenderCapable } from "../../../RenderEngine/RenderEnum/RenderCapable";
import { Shader3D } from "../../../RenderEngine/RenderShader/Shader3D";
import { Stat } from "../../../utils/Stat";
import { ComponentDriver } from "../../../components/ComponentDriver";
import { LayaEnv } from "../../../../LayaEnv";
import { SceneRenderManager } from "./SceneRenderManager";
import { VolumeManager } from "../../component/Volume/VolumeManager";
import { UI3DManager } from "../UI3D/UI3DManager";
import { Scene } from "../../../display/Scene";
import { ReflectionProbe } from "../../component/Volume/reflectionProbe/ReflectionProbe";
import { Color } from "../../../maths/Color";
import { Vector3 } from "../../../maths/Vector3";
import { Vector4 } from "../../../maths/Vector4";
import { RenderTexture } from "../../../resource/RenderTexture";
import { LayaGL } from "../../../layagl/LayaGL";
import { ShaderDataType } from "../../../RenderDriver/DriverDesign/RenderDevice/ShaderData";
import { Laya3DRender } from "../../RenderObjs/Laya3DRender";
import { RenderTexture2D } from "../../../resource/RenderTexture2D";
import { Viewport } from "../../../maths/Viewport";
import { Config } from "../../../../Config";
import { VolumetricGI } from "../../component/Volume/VolumetricGI/VolumetricGI";
export var FogMode;
(function (FogMode) {
    FogMode[FogMode["Linear"] = 0] = "Linear";
    FogMode[FogMode["EXP"] = 1] = "EXP";
    FogMode[FogMode["EXP2"] = 2] = "EXP2";
})(FogMode || (FogMode = {}));
export class Scene3D extends Sprite {
    static get _updateMark() {
        return RenderContext3D._instance._contextOBJ.cameraUpdateMask;
    }
    static set _updateMark(value) {
        RenderContext3D._instance._contextOBJ.cameraUpdateMask = value;
    }
    static regManager(type, cla) {
        Scene3D.componentManagerMap.set(type, cla);
    }
    static shaderValueInit() {
        Scene3DShaderDeclaration.SHADERDEFINE_FOG = Shader3D.getDefineByName("FOG");
        Scene3DShaderDeclaration.SHADERDEFINE_FOG_LINEAR = Shader3D.getDefineByName("FOG_LINEAR");
        Scene3DShaderDeclaration.SHADERDEFINE_FOG_EXP = Shader3D.getDefineByName("FOG_EXP");
        Scene3DShaderDeclaration.SHADERDEFINE_FOG_EXP2 = Shader3D.getDefineByName("FOG_EXP2");
        Scene3DShaderDeclaration.SHADERDEFINE_DIRECTIONLIGHT = Shader3D.getDefineByName("DIRECTIONLIGHT");
        Scene3DShaderDeclaration.SHADERDEFINE_POINTLIGHT = Shader3D.getDefineByName("POINTLIGHT");
        Scene3DShaderDeclaration.SHADERDEFINE_SPOTLIGHT = Shader3D.getDefineByName("SPOTLIGHT");
        Scene3DShaderDeclaration.SHADERDEFINE_SHADOW = Shader3D.getDefineByName("SHADOW");
        Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_CASCADE = Shader3D.getDefineByName("SHADOW_CASCADE");
        Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SOFT_SHADOW_LOW = Shader3D.getDefineByName("SHADOW_SOFT_SHADOW_LOW");
        Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SOFT_SHADOW_HIGH = Shader3D.getDefineByName("SHADOW_SOFT_SHADOW_HIGH");
        Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT = Shader3D.getDefineByName("SHADOW_SPOT");
        Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT_SOFT_SHADOW_LOW = Shader3D.getDefineByName("SHADOW_SPOT_SOFT_SHADOW_LOW");
        Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT_SOFT_SHADOW_HIGH = Shader3D.getDefineByName("SHADOW_SPOT_SOFT_SHADOW_HIGH");
        Scene3D.FOGCOLOR = Shader3D.propertyNameToID("u_FogColor");
        Scene3D.FOGPARAMS = Shader3D.propertyNameToID("u_FogParams");
        Scene3D.DIRECTIONLIGHTCOUNT = Shader3D.propertyNameToID("u_DirationLightCount");
        Scene3D.LIGHTBUFFER = Shader3D.propertyNameToID("u_LightBuffer");
        Scene3D.CLUSTERBUFFER = Shader3D.propertyNameToID("u_LightClusterBuffer");
        Scene3D.TIME = Shader3D.propertyNameToID("u_Time");
        Scene3D.GIRotate = Shader3D.propertyNameToID("u_GIRotate");
        let sceneUniformMap = Scene3D.sceneUniformMap = LayaGL.renderDeviceFactory.createGlobalUniformMap("Scene3D");
        sceneUniformMap.addShaderUniform(Scene3D.TIME, "u_Time", ShaderDataType.Float);
        sceneUniformMap.addShaderUniform(Scene3D.FOGPARAMS, "u_FogParams", ShaderDataType.Vector4);
        sceneUniformMap.addShaderUniform(Scene3D.FOGCOLOR, "u_FogColor", ShaderDataType.Color);
        sceneUniformMap.addShaderUniform(Scene3D.LIGHTBUFFER, "u_LightBuffer", ShaderDataType.Texture2D);
        sceneUniformMap.addShaderUniform(Scene3D.CLUSTERBUFFER, "u_LightClusterBuffer", ShaderDataType.Texture2D);
        sceneUniformMap.addShaderUniform(Scene3D.GIRotate, "u_GIRotate", ShaderDataType.Float);
        sceneUniformMap.addShaderUniform(Scene3D.DIRECTIONLIGHTCOUNT, "u_DirationLightCount", ShaderDataType.Int);
        ReflectionProbe.init();
        VolumetricGI.init();
    }
    static legacyLightingValueInit() {
        Scene3D.LIGHTDIRECTION = Shader3D.propertyNameToID("u_DirLightDirection");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.LIGHTDIRECTION, "u_DirLightDirection", ShaderDataType.Vector3);
        Scene3D.LIGHTDIRCOLOR = Shader3D.propertyNameToID("u_DirLightColor");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.LIGHTDIRCOLOR, "u_DirLightColor", ShaderDataType.Vector3);
        Scene3D.LIGHTMODE = Shader3D.propertyNameToID("u_DirLightMode");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.LIGHTMODE, "u_DirLightMode", ShaderDataType.Int);
        Scene3D.POINTLIGHTPOS = Shader3D.propertyNameToID("u_PointLightPos");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.POINTLIGHTPOS, "u_PointLightPos", ShaderDataType.Vector3);
        Scene3D.POINTLIGHTRANGE = Shader3D.propertyNameToID("u_PointLightRange");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.POINTLIGHTRANGE, "u_PointLightRange", ShaderDataType.Float);
        Scene3D.POINTLIGHTCOLOR = Shader3D.propertyNameToID("u_PointLightColor");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.POINTLIGHTCOLOR, "u_PointLightColor", ShaderDataType.Vector3);
        Scene3D.POINTLIGHTMODE = Shader3D.propertyNameToID("u_PointLightMode");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.POINTLIGHTMODE, "u_PointLightMode", ShaderDataType.Int);
        Scene3D.SPOTLIGHTPOS = Shader3D.propertyNameToID("u_SpotLightPos");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.SPOTLIGHTPOS, "u_SpotLightPos", ShaderDataType.Vector3);
        Scene3D.SPOTLIGHTDIRECTION = Shader3D.propertyNameToID("u_SpotLightDirection");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.SPOTLIGHTDIRECTION, "u_SpotLightDirection", ShaderDataType.Vector3);
        Scene3D.SPOTLIGHTSPOTANGLE = Shader3D.propertyNameToID("u_SpotLightSpot");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.SPOTLIGHTSPOTANGLE, "u_SpotLightSpot", ShaderDataType.Float);
        Scene3D.SPOTLIGHTRANGE = Shader3D.propertyNameToID("u_SpotLightRange");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.SPOTLIGHTRANGE, "u_SpotLightRange", ShaderDataType.Float);
        Scene3D.SPOTLIGHTCOLOR = Shader3D.propertyNameToID("u_SpotLightColor");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.SPOTLIGHTCOLOR, "u_SpotLightColor", ShaderDataType.Vector3);
        Scene3D.SPOTLIGHTMODE = Shader3D.propertyNameToID("u_SpotLightMode");
        Scene3D.sceneUniformMap.addShaderUniform(Scene3D.SPOTLIGHTMODE, "u_SpotLightMode", ShaderDataType.Int);
    }
    static __init__() {
        var multiLighting = Config3D._multiLighting;
        if (multiLighting) {
            const width = 4;
            var maxLightCount = Config3D.maxLightCount;
            var clusterSlices = Config3D.lightClusterCount;
            Cluster.instance = new Cluster(clusterSlices.x, clusterSlices.y, clusterSlices.z, Math.min(Config3D.maxLightCount, Config3D._maxAreaLightCountPerClusterAverage));
            Scene3D._lightTexture = Utils3D._createFloatTextureBuffer(width, maxLightCount);
            Scene3D._lightTexture.lock = true;
            Scene3D._lightPixles = new Float32Array(maxLightCount * width * 4);
        }
        Scene3D.shaderValueInit();
        var configShaderValue = Shader3D._configDefineValues;
        if (!Config3D._multiLighting) {
            (configShaderValue.add(Shader3D.SHADERDEFINE_LEGACYSINGALLIGHTING));
            Scene3D.legacyLightingValueInit();
        }
        Scene3D._shadowCasterPass = new ShadowCasterPass();
        if (Config._uniformBlock)
            configShaderValue.add(Shader3D.SHADERDEFINE_ENUNIFORMBLOCK);
        let supportFloatTex = LayaGL.renderEngine.getCapable(RenderCapable.TextureFormat_R32G32B32A32);
        if (supportFloatTex) {
            configShaderValue.add(Shader3D.SHADERDEFINE_FLOATTEXTURE);
        }
        let supportFloatLinearFiltering = LayaGL.renderEngine.getCapable(RenderCapable.Texture_FloatLinearFiltering);
        if (supportFloatLinearFiltering) {
            configShaderValue.add(Shader3D.SHADERDEFINE_FLOATTEXTURE_FIL_LINEAR);
        }
        let supportStorageBuffer = LayaGL.renderEngine.getCapable(RenderCapable.StorageBuffer);
        if (supportStorageBuffer)
            configShaderValue.add(Shader3D.SHADERDEFINE_STORAGEBUFFER);
    }
    static load(url, complete) {
        ILaya.loader.load(url).then((res) => {
            if (complete) {
                let ret;
                if (res) {
                    let scene = res.create();
                    if (scene instanceof Scene)
                        ret = scene._scene3D;
                    else
                        ret = scene;
                }
                complete.runWith([ret]);
            }
        });
    }
    get scene2D() {
        return this._scene2D;
    }
    get sceneRenderableManager() {
        return this._sceneRenderManager;
    }
    set sceneRenderableManager(manager) {
        manager.list = this._sceneRenderManager.list;
        this._sceneRenderManager = manager;
    }
    get enableFog() {
        return this._enableFog;
    }
    set enableFog(value) {
        if (this._enableFog !== value) {
            this._enableFog = value;
            if (value) {
                this._shaderValues.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG);
            }
            else
                this._shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG);
        }
    }
    get fogMode() {
        return this._fogMode;
    }
    set fogMode(value) {
        this._fogMode = value;
        switch (value) {
            case FogMode.Linear:
                this._shaderValues.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG_LINEAR);
                this._shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG_EXP);
                this._shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG_EXP2);
                break;
            case FogMode.EXP:
                this._shaderValues.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG_EXP);
                this._shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG_LINEAR);
                this._shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG_EXP2);
                break;
            case FogMode.EXP2:
                this._shaderValues.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG_EXP2);
                this._shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG_LINEAR);
                this._shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_FOG_EXP);
                break;
        }
    }
    get fogColor() {
        return this._shaderValues.getColor(Scene3D.FOGCOLOR);
    }
    set fogColor(value) {
        this._shaderValues.setColor(Scene3D.FOGCOLOR, value);
    }
    get fogStart() {
        return this._fogParams.x;
    }
    set fogStart(value) {
        this._fogParams.x = value;
        this.fogParams = this._fogParams;
    }
    get fogEnd() {
        return this._fogParams.y;
    }
    set fogEnd(value) {
        this._fogParams.y = value;
        this.fogParams = this._fogParams;
    }
    get fogDensity() {
        return this._fogParams.z;
    }
    set fogDensity(value) {
        this._fogParams.z = value;
        this.fogParams = this._fogParams;
    }
    get fogParams() {
        return this._shaderValues.getVector(Scene3D.FOGPARAMS);
    }
    set fogParams(value) {
        this._shaderValues.setVector(Scene3D.FOGPARAMS, value);
    }
    get GIRotate() {
        return this._shaderValues.getNumber(Scene3D.GIRotate);
    }
    set GIRotate(value) {
        this._shaderValues.setNumber(Scene3D.GIRotate, value);
    }
    get ambientMode() {
        return this._sceneReflectionProb.ambientMode;
    }
    set ambientMode(value) {
        this._sceneReflectionProb.ambientMode = value;
    }
    get sceneReflectionProb() {
        return this._sceneReflectionProb;
    }
    set sceneReflectionProb(value) {
        this._sceneReflectionProb = value;
    }
    get ambientColor() {
        return this._sceneReflectionProb.ambientColor;
    }
    set ambientColor(value) {
        this._sceneReflectionProb.ambientColor = value;
    }
    get ambientIntensity() {
        return this._sceneReflectionProb.ambientIntensity;
    }
    set ambientIntensity(value) {
        this._sceneReflectionProb.ambientIntensity = value;
    }
    get reflectionIntensity() {
        return this._sceneReflectionProb.reflectionIntensity;
    }
    set reflectionIntensity(value) {
        this._sceneReflectionProb.reflectionIntensity = value;
    }
    get ambientSH() {
        return this._sceneReflectionProb.ambientSH;
    }
    set ambientSH(value) {
        this._sceneReflectionProb.ambientSH = value;
    }
    get iblTex() {
        return this._sceneReflectionProb.iblTex;
    }
    set iblTex(value) {
        this._sceneReflectionProb.iblTex = value;
    }
    get iblTexRGBD() {
        return this._sceneReflectionProb.iblTexRGBD;
    }
    set iblTexRGBD(value) {
        this._sceneReflectionProb.iblTexRGBD = value;
    }
    get skyRenderer() {
        return this._skyRenderer;
    }
    get physicsSimulation() {
        return this._physicsManager;
    }
    get timer() {
        return this._timer;
    }
    set timer(value) {
        this._timer = value;
    }
    get lightmaps() {
        return this._lightmaps.slice();
    }
    set lightmaps(value) {
        var maps = this._lightmaps;
        if (maps) {
            for (var i = 0, n = maps.length; i < n; i++) {
                var map = maps[i];
                map.lightmapColor && map.lightmapColor._removeReference();
                map.lightmapDirection && map.lightmapDirection._removeReference();
            }
        }
        if (value) {
            var count = value.length;
            maps.length = count;
            for (i = 0; i < count; i++) {
                var map = value[i];
                map.lightmapColor && map.lightmapColor._addReference();
                map.lightmapDirection && map.lightmapDirection._addReference();
                maps[i] = map;
            }
        }
        else {
            maps.length = 0;
        }
        this._sceneModuleData.lightmapDirtyFlag = Scene3D._updateMark;
    }
    get shadowMapFrequency() {
        return this._ShadowMapupdateFrequency;
    }
    set shadowMapFrequency(value) {
        this._ShadowMapupdateFrequency = value;
    }
    constructor() {
        super();
        this._reflectionsSource = 0;
        this._reflectionsResolution = "256";
        this._reflectionsIblSamples = 128;
        this._lightCount = 0;
        this._pointLights = new LightQueue();
        this._spotLights = new LightQueue();
        this._directionLights = new LightQueue();
        this._alternateLights = new AlternateLightQueue();
        this._lightmaps = [];
        this._skyRenderer = new SkyRenderer();
        this._time = 0;
        this._physicsStepTime = 0;
        this._sunColor = new Color(1.0, 1.0, 1.0);
        this._sundir = new Vector3();
        this._collsionTestList = [];
        this._key = new SubmitKey();
        this._cameraPool = [];
        this._UI3DManager = new UI3DManager();
        this.currentCreationLayer = Math.pow(2, 0);
        this.enableLight = true;
        this._ShadowMapupdateFrequency = 1;
        this.componentElementMap = new Map();
        this._componentElementDatasMap = {};
        this._nodeType = 1;
        this._componentDriver = new ComponentDriver();
        this._timer = ILaya.timer;
        this._sceneModuleData = Laya3DRender.Render3DModuleDataFactory.createSceneModuleData();
        if (LayaEnv.isConch && window.conchConfig.getGraphicsAPI() != 2) {
            this._nativeObj = new window.conchSubmitScene3D(this.renderSubmit.bind(this));
        }
        if (ILaya.Laya3D.enablePhysics)
            this._physicsManager = ILaya.Laya3D.PhysicsCreateUtil.createPhysicsManger(Scene3D.physicsSettings);
        this._shaderValues = LayaGL.renderDeviceFactory.createShaderData(null);
        this._shaderValues.addDefines(Shader3D._configDefineValues);
        this._fogParams = new Vector4(300, 1000, 0.01, 0);
        this.enableFog = false;
        this.fogStart = 300;
        this.fogEnd = 1000;
        this.fogDensity = 0.01;
        this.fogColor = new Color(0.7, 0.7, 0.7);
        this.fogMode = FogMode.Linear;
        this.GIRotate = 0;
        this._scene = this;
        this._sceneRenderManager = new SceneRenderManager();
        if (Config3D.debugFrustumCulling) {
        }
        this._volumeManager = new VolumeManager();
        this._UI3DManager = new UI3DManager();
        this.sceneReflectionProb = this._volumeManager.reflectionProbeManager.sceneReflectionProbe;
        this._sceneReflectionProb.reflectionIntensity = 1.0;
        this.ambientColor = new Color(0.212, 0.227, 0.259);
        Scene3D.componentManagerMap.forEach((val, key) => {
            this.componentElementMap.set(key, new val());
        });
    }
    get componentElementDatasMap() {
        return this._componentElementDatasMap;
    }
    set componentElementDatasMap(value) {
        this._componentElementDatasMap = value;
        this.componentElementMap.forEach((value, key) => {
            value.Init(this._componentElementDatasMap[key]);
        });
    }
    _setStructParent(value) {
        let struct = this._struct;
        if (struct.parent) {
            struct.parent.removeChild(struct);
            struct.parent = null;
        }
        if (value && value._struct) {
            if (value.is3D) {
                value._struct.addChild(struct, value._struct.children.length);
            }
            else {
                let index = value._children.indexOf(this);
                value._struct.addChild(struct, index);
            }
        }
    }
    _update() {
        var delta = this.timer.delta / 1000;
        this._time += delta;
        this._shaderValues.setNumber(Scene3D.TIME, this._time);
        if (LayaEnv.isPlaying) {
            this._physicsStepTime += delta;
            let steps = Math.floor(this._physicsStepTime / Scene3D.physicsSettings.fixedTimeStep);
            steps = Math.min(steps, Scene3D.physicsSettings.maxSubSteps);
            if (steps > 0) {
                let physicsManager = this._physicsManager;
                if (ILaya.Laya3D.enablePhysics && Stat.enablePhysicsUpdate) {
                    physicsManager.update(steps * Scene3D.physicsSettings.fixedTimeStep);
                }
                this._physicsStepTime -= steps * Scene3D.physicsSettings.fixedTimeStep;
            }
        }
        this._componentDriver.callStart();
        this._componentDriver.callUpdate();
        this._componentDriver.callLateUpdate();
        this._componentDriver.callDestroy();
        if (this._volumeManager.needreCaculateAllRenderObjects())
            this._volumeManager.reCaculateAllRenderObjects(this._sceneRenderManager.list);
        else
            this._volumeManager.handleMotionlist();
        this.componentElementMap.forEach((value) => {
            value.update(delta);
        });
        this._sceneRenderManager.renderUpdate();
        this.skyRenderer.renderUpdate(RenderContext3D._instance);
        if (!this._renderByEditor)
            this._UI3DManager.update();
    }
    _binarySearchIndexInCameraPool(camera) {
        var start = 0;
        var end = this._cameraPool.length - 1;
        var mid;
        while (start <= end) {
            mid = Math.floor((start + end) / 2);
            var midValue = this._cameraPool[mid]._renderingOrder;
            if (midValue == camera._renderingOrder)
                return mid;
            else if (midValue > camera._renderingOrder)
                end = mid - 1;
            else
                start = mid + 1;
        }
        return start;
    }
    _getGroup() {
        return this._group;
    }
    _setGroup(value) {
        this._group = value;
    }
    _onActive() {
        super._onActive();
        ILaya.stage._scene3Ds.push(this);
    }
    _onInActive() {
        super._onInActive();
        var scenes = ILaya.stage._scene3Ds;
        scenes.splice(scenes.indexOf(this), 1);
    }
    _prepareSceneToRender() {
        var shaderValues = this._shaderValues;
        var multiLighting = Config3D._multiLighting && Stat.enableMulLight;
        if (multiLighting) {
            var ligTex = Scene3D._lightTexture;
            var ligPix = Scene3D._lightPixles;
            const pixelWidth = ligTex.width;
            const floatWidth = pixelWidth * 4;
            var curCount = 0;
            var dirCount = Stat.enableLight ? this._directionLights._length : 0;
            var dirElements = this._directionLights._elements;
            if (dirCount > 0) {
                var sunLightIndex = this._directionLights.getBrightestLight();
                this._mainDirectionLight = dirElements[sunLightIndex];
                this._directionLights.normalLightOrdering(sunLightIndex);
                for (var i = 0; i < dirCount; i++, curCount++) {
                    var dirLight = dirElements[i];
                    var dir = dirLight.direction;
                    var intCor = dirLight._intensityColor;
                    var off = floatWidth * curCount;
                    intCor.x = Color.gammaToLinearSpace(dirLight.color.r);
                    intCor.y = Color.gammaToLinearSpace(dirLight.color.g);
                    intCor.z = Color.gammaToLinearSpace(dirLight.color.b);
                    Vector3.scale(intCor, dirLight._intensity, intCor);
                    dirLight.owner.transform.worldMatrix.getForward(dir);
                    Vector3.normalize(dir, dir);
                    ligPix[off] = intCor.x;
                    ligPix[off + 1] = intCor.y;
                    ligPix[off + 2] = intCor.z;
                    ligPix[off + 3] = dirLight._lightmapBakedType;
                    ligPix[off + 4] = dir.x;
                    ligPix[off + 5] = dir.y;
                    ligPix[off + 6] = dir.z;
                    if (i === 0) {
                        this._sunColor = dirLight.color;
                        this._sundir = dir;
                    }
                }
                shaderValues.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_DIRECTIONLIGHT);
            }
            else {
                shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_DIRECTIONLIGHT);
                this._mainDirectionLight = null;
            }
            var poiCount = Stat.enableLight ? this._pointLights._length : 0;
            if (poiCount > 0) {
                var poiElements = this._pointLights._elements;
                var mainPointLightIndex = this._pointLights.getBrightestLight();
                this._mainPointLight = poiElements[mainPointLightIndex];
                this._pointLights.normalLightOrdering(mainPointLightIndex);
                for (var i = 0; i < poiCount; i++, curCount++) {
                    var poiLight = poiElements[i];
                    var pos = poiLight.owner.transform.position;
                    var intCor = poiLight._intensityColor;
                    var off = floatWidth * curCount;
                    intCor.x = Color.gammaToLinearSpace(poiLight.color.r);
                    intCor.y = Color.gammaToLinearSpace(poiLight.color.g);
                    intCor.z = Color.gammaToLinearSpace(poiLight.color.b);
                    Vector3.scale(intCor, poiLight._intensity, intCor);
                    ligPix[off] = intCor.x;
                    ligPix[off + 1] = intCor.y;
                    ligPix[off + 2] = intCor.z;
                    ligPix[off + 3] = poiLight.range;
                    ligPix[off + 4] = pos.x;
                    ligPix[off + 5] = pos.y;
                    ligPix[off + 6] = pos.z;
                    ligPix[off + 7] = poiLight._lightmapBakedType;
                }
                shaderValues.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_POINTLIGHT);
            }
            else {
                shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_POINTLIGHT);
                this._mainPointLight = null;
            }
            var spoCount = Stat.enableLight ? this._spotLights._length : 0;
            if (spoCount > 0) {
                var spoElements = this._spotLights._elements;
                var mainSpotLightIndex = this._spotLights.getBrightestLight();
                this._mainSpotLight = spoElements[mainSpotLightIndex];
                this._spotLights.normalLightOrdering(mainSpotLightIndex);
                for (var i = 0; i < spoCount; i++, curCount++) {
                    var spoLight = spoElements[i];
                    var dir = spoLight.direction;
                    var pos = spoLight.owner.transform.position;
                    var intCor = spoLight._intensityColor;
                    var off = floatWidth * curCount;
                    intCor.x = Color.gammaToLinearSpace(spoLight.color.r);
                    intCor.y = Color.gammaToLinearSpace(spoLight.color.g);
                    intCor.z = Color.gammaToLinearSpace(spoLight.color.b);
                    Vector3.scale(intCor, spoLight._intensity, intCor);
                    spoLight.owner.transform.worldMatrix.getForward(dir);
                    Vector3.normalize(dir, dir);
                    ligPix[off] = intCor.x;
                    ligPix[off + 1] = intCor.y;
                    ligPix[off + 2] = intCor.z;
                    ligPix[off + 3] = spoLight.range;
                    ligPix[off + 4] = pos.x;
                    ligPix[off + 5] = pos.y;
                    ligPix[off + 6] = pos.z;
                    ligPix[off + 7] = spoLight.spotAngle * Math.PI / 180;
                    ligPix[off + 8] = dir.x;
                    ligPix[off + 9] = dir.y;
                    ligPix[off + 10] = dir.z;
                    ligPix[off + 11] = spoLight._lightmapBakedType;
                }
                shaderValues.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SPOTLIGHT);
            }
            else {
                shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SPOTLIGHT);
                this._mainSpotLight = null;
            }
            (curCount > 0) && (ligTex.setSubPixelsData(0, 0, pixelWidth, curCount, ligPix, 0, false, false, false));
            shaderValues.setTexture(Scene3D.LIGHTBUFFER, ligTex);
            shaderValues.setInt(Scene3D.DIRECTIONLIGHTCOUNT, this._directionLights._length);
            shaderValues.setTexture(Scene3D.CLUSTERBUFFER, Cluster.instance._clusterTexture);
        }
        else {
            if (!Scene3D.LIGHTDIRECTION)
                Scene3D.legacyLightingValueInit();
            if (this._directionLights._length > 0 && Stat.enableLight) {
                var dirLight = this._directionLights._elements[0];
                this._mainDirectionLight = dirLight;
                dirLight._intensityColor.x = Color.gammaToLinearSpace(dirLight.color.r);
                dirLight._intensityColor.y = Color.gammaToLinearSpace(dirLight.color.g);
                dirLight._intensityColor.z = Color.gammaToLinearSpace(dirLight.color.b);
                Vector3.scale(dirLight._intensityColor, dirLight._intensity, dirLight._intensityColor);
                dirLight.owner.transform.worldMatrix.getForward(dirLight.direction);
                Vector3.normalize(dirLight.direction, dirLight.direction);
                shaderValues.setVector3(Scene3D.LIGHTDIRCOLOR, dirLight._intensityColor);
                shaderValues.setVector3(Scene3D.LIGHTDIRECTION, dirLight.direction);
                shaderValues.setInt(Scene3D.LIGHTMODE, dirLight._lightmapBakedType);
                if (i === 0) {
                    this._sunColor = dirLight.color;
                    this._sundir = dirLight.direction;
                }
                shaderValues.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_DIRECTIONLIGHT);
            }
            else {
                shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_DIRECTIONLIGHT);
            }
            if (this._pointLights._length > 0 && Stat.enableLight) {
                var poiLight = this._pointLights._elements[0];
                this._mainPointLight = poiLight;
                poiLight._intensityColor.x = Color.gammaToLinearSpace(poiLight.color.r);
                poiLight._intensityColor.y = Color.gammaToLinearSpace(poiLight.color.g);
                poiLight._intensityColor.z = Color.gammaToLinearSpace(poiLight.color.b);
                Vector3.scale(poiLight._intensityColor, poiLight._intensity, poiLight._intensityColor);
                shaderValues.setVector3(Scene3D.POINTLIGHTCOLOR, poiLight._intensityColor);
                shaderValues.setVector3(Scene3D.POINTLIGHTPOS, poiLight.owner.transform.position);
                shaderValues.setNumber(Scene3D.POINTLIGHTRANGE, poiLight.range);
                shaderValues.setInt(Scene3D.POINTLIGHTMODE, poiLight._lightmapBakedType);
                shaderValues.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_POINTLIGHT);
            }
            else {
                shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_POINTLIGHT);
            }
            if (this._spotLights._length > 0 && Stat.enableLight) {
                var spotLight = this._spotLights._elements[0];
                this._mainSpotLight = spotLight;
                spotLight._intensityColor.x = Color.gammaToLinearSpace(spotLight.color.r);
                spotLight._intensityColor.y = Color.gammaToLinearSpace(spotLight.color.g);
                spotLight._intensityColor.z = Color.gammaToLinearSpace(spotLight.color.b);
                Vector3.scale(spotLight._intensityColor, spotLight._intensity, spotLight._intensityColor);
                shaderValues.setVector3(Scene3D.SPOTLIGHTCOLOR, spotLight._intensityColor);
                shaderValues.setVector3(Scene3D.SPOTLIGHTPOS, spotLight.owner.transform.position);
                spotLight.owner.transform.worldMatrix.getForward(spotLight.direction);
                Vector3.normalize(spotLight.direction, spotLight.direction);
                shaderValues.setVector3(Scene3D.SPOTLIGHTDIRECTION, spotLight.direction);
                shaderValues.setNumber(Scene3D.SPOTLIGHTRANGE, spotLight.range);
                shaderValues.setNumber(Scene3D.SPOTLIGHTSPOTANGLE, spotLight.spotAngle * Math.PI / 180);
                shaderValues.setInt(Scene3D.SPOTLIGHTMODE, spotLight._lightmapBakedType);
                shaderValues.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SPOTLIGHT);
            }
            else {
                shaderValues.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SPOTLIGHT);
            }
        }
    }
    get cullInfoCamera() {
        return this._cullInfoCamera;
    }
    _setCullCamera(camera) {
        this._cullInfoCamera = camera;
        if (camera) {
            this.skyRenderer.setRenderElement(camera.skyRenderElement);
        }
        else {
            this.skyRenderer.setRenderElement(null);
        }
    }
    recaculateCullCamera() {
        this._setCullCamera(this._cameraPool[0]);
        this._cameraPool.forEach(element => {
            if (this.cullInfoCamera.maxlocalYDistance < element.maxlocalYDistance) {
                this._setCullCamera(element);
            }
        });
    }
    _addCamera(camera) {
        var index = this._binarySearchIndexInCameraPool(camera);
        var order = camera._renderingOrder;
        var count = this._cameraPool.length;
        while (index < count && this._cameraPool[index]._renderingOrder <= order)
            index++;
        this._cameraPool.splice(index, 0, camera);
    }
    _removeCamera(camera) {
        this._cameraPool.splice(this._cameraPool.indexOf(camera), 1);
    }
    _addRenderObject(render) {
        this._sceneRenderManager.addRenderObject(render);
        render._inRenderList = true;
        render._addReflectionProbeUpdate();
    }
    _removeRenderObject(render) {
        render._inRenderList = false;
        this._sceneRenderManager.removeRenderObject(render);
    }
    _setBelongScene(scene) {
    }
    destroy(destroyChild = true) {
        if (this._destroyed)
            return;
        super.destroy(destroyChild);
        this._skyRenderer.destroy();
        this._skyRenderer = null;
        this._directionLights = null;
        this._pointLights = null;
        this._spotLights = null;
        this._alternateLights = null;
        (RenderContext3D._instance.scene == this) && (RenderContext3D._instance.scene = null);
        this._shaderValues.destroy();
        this._shaderValues = null;
        this.sceneRenderableManager.destroy();
        this._sceneRenderManager = null;
        this._cameraPool = null;
        this._physicsManager && this._physicsManager.destroy();
        var maps = this._lightmaps;
        if (maps) {
            for (var i = 0, n = maps.length; i < n; i++) {
                var map = maps[i];
                map.lightmapColor && map.lightmapColor._removeReference();
                map.lightmapDirection && map.lightmapDirection._removeReference();
            }
        }
        this._lightmaps = null;
        this._volumeManager.destroy();
        this._componentDriver.callDestroy();
    }
    getComponentElementManager(type) {
        return this.componentElementMap.get(type);
    }
    renderSubmit() {
        if (this._children.length <= 0)
            return;
        if (this._renderByEditor)
            return;
        this._prepareSceneToRender();
        var i, n, n1;
        Scene3D._updateMark++;
        for (i = 0, n = this._cameraPool.length, n1 = n - 1; i < n; i++) {
            var camera = this._cameraPool[i];
            if (camera.enableRender && camera.activeInHierarchy) {
                if (camera.renderTarget) {
                    camera.enableBuiltInRenderTexture || (camera.enableBuiltInRenderTexture = false);
                }
                else {
                    camera.enableBuiltInRenderTexture || (camera.enableBuiltInRenderTexture = true);
                }
                camera.render(this);
                if (!camera._offScreenRenderTexture) {
                    this.blitMainCanvas(camera._internalRenderTexture, camera.normalizedViewport, camera);
                }
                camera._aftRenderMainPass();
            }
        }
        RenderTexture.clearPool();
    }
    blitMainCanvas(source, normalizeViewPort, camera) {
        if (!source)
            return;
        Scene3D.mainCavansViewPort.x = RenderContext3D.clientWidth * normalizeViewPort.x | 0;
        Scene3D.mainCavansViewPort.y = RenderContext3D.clientHeight * normalizeViewPort.y | 0;
        Scene3D.mainCavansViewPort.width = RenderContext3D.clientWidth * normalizeViewPort.width | 0;
        Scene3D.mainCavansViewPort.height = RenderContext3D.clientHeight * normalizeViewPort.height | 0;
        source.filterMode = FilterMode.Bilinear;
        if (camera.fxaa)
            BlitFrameBufferCMD.shaderdata.addDefine(BaseCamera.SHADERDEFINE_FXAA);
        var cmd = BlitFrameBufferCMD.create(source, null, Scene3D.mainCavansViewPort, null, null, BlitFrameBufferCMD.shaderdata);
        cmd.run();
        cmd.recover();
        RenderTexture2D._clear = false;
        BlitFrameBufferCMD.shaderdata.removeDefine(BaseCamera.SHADERDEFINE_FXAA);
    }
    setGlobalShaderValue(name, type, value) {
        var shaderOffset = Shader3D.propertyNameToID(name);
        this._shaderValues.setShaderData(shaderOffset, type, value);
    }
    get fogRange() {
        return this._fogParams.y - this.fogParams.x;
    }
    set fogRange(value) {
        this._fogParams.y = value + this.fogParams.x;
        this.fogParams = this._fogParams;
    }
    setlightmaps(value) {
        var maps = this._lightmaps;
        for (var i = 0, n = maps.length; i < n; i++)
            maps[i].lightmapColor._removeReference();
        if (value) {
            var count = value.length;
            maps.length = count;
            for (i = 0; i < count; i++) {
                var lightMap = value[i];
                lightMap._addReference();
                (maps[i]) || (maps[i] = new Lightmap());
                maps[i].lightmapColor = lightMap;
            }
        }
        else {
            throw new Error("lightmap can't be null.");
        }
    }
    getlightmaps() {
        var lightmapColors = new Array(this._lightmaps.length);
        for (var i = 0; i < this._lightmaps.length; i++) {
            lightmapColors[i] = this._lightmaps[i].lightmapColor;
        }
        return lightmapColors;
    }
}
Scene3D.physicsSettings = new PhysicsSettings();
Scene3D.mainCavansViewPort = new Viewport(0, 0, 1, 1);
Scene3D.componentManagerMap = new Map();
